import CategoryWell from 'owa-categories/lib/components/CategoryWell';
import type { MailListItemFullThreeColumnProps } from './MailListItemComponentProps';
import {
    getConditionalFormattingStyles,
    ConditionalFormattingSurface,
} from 'owa-conditional-formatting';
import onCategoryClicked from '../utils/onCategoryClicked';
import renderReactions from '../utils/renderReactions';
import isValidEmailAddress from '../utils/isValidEmailAddress';
import classnames from 'owa-classnames';
import { observer } from 'owa-mobx-react';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import loc from 'owa-localize';
import {
    showPersonaCheckbox,
    onMailListItemClickHandler,
    useMailListItemContainerContext,
} from 'owa-mail-list-item-shared';
import getListViewStore from 'owa-mail-list-store/lib/store/Store';
import {
    full,
    medium,
    compact,
    reactionsContainer,
    containerForTwistyAndCheckboxSCL,
    containerPaddingCompact,
    containerPaddingNoCheckboxFull,
    containerPaddingNoCheckboxMedium,
    multiSelectNoTwistyDisplayed,
    secondRowThreeColumnUnread,
    secondRowThreeColumn,
    secondRowThreeColumnPaddingNext,
    conversationHeaderContent,
    categoryContainerThreeColumn,
    categoryContainerThreeColumnFull,
    categoryContainerThreeColumnMediumOrCompact,
    sclContainer,
    firstColumnThreeColumn,
    firstColumnThreeColumnNoTwisty,
    column,
    multiSelectNoCheckbox,
    secondRowThreeColumnRead,
    secondLineTextThreeColumn,
    categoryWellThreeColumn,
    previewDisplayThreeColumn,
} from 'owa-mail-listitem-styles/lib/scss/MailListItem.scss';
import {
    firstRowThreeColumnUnreadNext,
    firstRowThreeColumnNext,
} from 'owa-mail-listitem-styles/lib/scss/ListViewItemCommonStyles.scss';
import {
    previewDisplayThreeColumnCategories,
    threeColFolderTag,
} from './MailListItemFullThreeColumn.scss';
import { useMessageListTextStyle } from 'owa-mail-listitem-styles/lib/utils/useMessageListTextStyle';
import MailListItemSelectionSource from 'owa-mail-store/lib/store/schema/MailListItemSelectionSource';
import shouldShowUnstackedReadingPane from 'owa-mail-organization-settings/lib/utils/shouldShowUnstackedReadingPane';
import type { TableView, MailFolderTableQuery } from 'owa-mail-list-store';
import { isMailListConversationView } from 'owa-mail-list-store';
import { type SortColumn } from 'owa-mail-list-store/lib/store/schema/SortBy';
import getInstrumentationContextsFromTableView from 'owa-mail-list-store/lib/utils/getInstrumentationContextsFromTableView';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import type SingleRecipientType from 'owa-service/lib/contract/SingleRecipientType';
import isSenderImageEnabled from 'owa-messagelist-layout-option/lib/utils/isSenderImageEnabled';
import React from 'react';
import {
    MailListItemFirstLineText,
    MailListItemIconBar,
    MailListItemLabelsContainer,
    MailListItemLineText,
    MailListItemRichPreviewWell,
    MailListItemTimestamp,
    MailListItemTwistyButton,
    MailListItemUnreadCount,
    PersonaCheckbox,
    FolderTag,
    MailListItemMeetingInfo,
    MailListItemPreviewDisplay,
    MailListItemMarker,
    MailListItemUnreadBar,
} from 'owa-mail-list-item-components';
import { calculateAttachmentPreviewWellViewState } from 'owa-mail-layout';
import { senderEmailNotFoundLabel } from './MailListItem.locstring.json';
import { SuggestedUnsubscribe } from 'owa-suggested-unsubscribe-peek';
import { getDensityModeCssClass, getDensityModeString, getIsDarkTheme } from 'owa-fabric-theme';
import { CopilotInboxPriorityIcon } from 'owa-mail-copilot-inbox-shared';

const defaultExport: React.FunctionComponent<MailListItemFullThreeColumnProps> = observer(
    function MailListItemFullThreeColumn(props: MailListItemFullThreeColumnProps): JSX.Element {
        const {
            canArchive,
            canDelete,
            canPin,
            categories,
            conversationId,
            copilotInboxScore,
            effectiveMentioned,
            firstLineText,
            firstLineTooltipText,
            hasAttachment,
            hasSharepointLink,
            highlightTerms,
            importance,
            isComplete,
            isFirstLevelExpanded,
            isFlagged,
            isInArchiveFolder,
            isInNotesFolder,
            isInOutboxFolder,
            isInJunkFolder,
            isLoadingSecondLevelExpansion,
            isPinned,
            renderMinimum,
            isSearchTable,
            isSecondLevelExpanded,
            isSelected,
            isSnoozed,
            isTaggedForBigScreen,
            isUnauthenticatedSender,
            itemClassIcon,
            lastDeliveryTimestamp,
            lastSender,
            latestItemId,
            listViewType,
            mailboxGuids,
            parentFolderDisplayName,
            parentFolderId,
            returnTime,
            rowId,
            rowKey,
            secondLineText,
            secondLineTooltipText,
            shouldShowExternalLabel,
            shouldShowRSVP,
            shouldShowTwisty,
            shouldSuggestUnsubscribe,
            showAttachmentPreview,
            showDraftsIndicator,
            showFolderTag,
            showPreviewText,
            smimeType,
            styleSelectorAsPerUserSettings,
            supportsFlagging,
            supportsHoverIcons,
            supportsMove,
            supportsPinning,
            tableViewId,
            thirdLineText,
            unreadCount,
            isReminderSet,
            isScheduledSend,
            bulkCategory,
            isCfPreview,
            cfPreviewMods,
            cfPreviewBackgroundColorStyle,
            cfPreviewUnreadBarVars,
        } = props;

        const tableView: TableView = getListViewStore().tableViews.get(tableViewId) as TableView;
        const { conditionalFormattingModifications: contextCfModifications } =
            useMailListItemContainerContext();
        const isDarkTheme = getIsDarkTheme();
        const conditionalFormattingModifications = cfPreviewMods
            ? cfPreviewMods
            : contextCfModifications;
        const instrumentationContext = getInstrumentationContextsFromTableView(
            [rowKey],
            tableView
        )[0];

        const mailTableQuery = tableView?.tableQuery as MailFolderTableQuery;
        const isCategorySort = mailTableQuery?.sortBy?.sortColumn === 14;
        const isPrioritySort = mailTableQuery?.sortBy?.sortColumn === 16;

        const densityModeString = getDensityModeCssClass(full, medium, compact);
        // derived properties based on props/flights
        const isUnread = unreadCount > 0;
        const showCategories = categories?.length > 0 && !isCategorySort;
        const showCategoriesInline =
            shouldShowUnstackedReadingPane() && isFirstLevelExpanded && !isCategorySort;
        const notExpanded =
            !isFirstLevelExpanded && !isSecondLevelExpanded && !isLoadingSecondLevelExpansion;
        const showPreviews = !shouldShowUnstackedReadingPane() || notExpanded;
        const showMultiSelectButton = !isSearchTable;
        const senderImageEnabled = isSenderImageEnabled();

        const showCheckbox = showPersonaCheckbox(isSearchTable, tableView) && !isCfPreview;

        const mailboxInfo = props.mailboxInfo ?? getMailboxInfo(tableView);
        const isConversationView = isMailListConversationView(tableView);

        /**
         * Categories and folder tags are shown on the same line as preview text. When
         * preview text is not shown, additional container is used to display categories and folder tags.
         */
        const showLabelsContainer = !showPreviewText && (showCategories || showFolderTag);

        // To ensure that preview text is not displayed for Newest at the top/bottom setting with conversation expansion
        const showPreviewTextConditionally = showPreviewText && notExpanded;

        const personaCheckboxComponent = (
            <PersonaCheckbox
                tableViewId={tableViewId}
                isSelected={isSelected}
                lastSender={lastSender}
                rowKey={rowKey}
                lastDeliveryTimestamp={lastDeliveryTimestamp}
                isUnauthenticatedSender={isUnauthenticatedSender}
                isCFPreview={isCfPreview}
            />
        );

        const conditionalFormattingTextStyle = React.useMemo(() => {
            if (
                isGlobalFeatureEnabled('tri-conditional-formatting-v2') &&
                conditionalFormattingModifications &&
                conditionalFormattingModifications.length
            ) {
                return getConditionalFormattingStyles(
                    ConditionalFormattingSurface.Text,
                    conditionalFormattingModifications,
                    isDarkTheme,
                    isSelected
                );
            }
            return undefined;
        }, [conditionalFormattingModifications, isDarkTheme, isSelected]);

        const densityMode = getDensityModeString();
        const twistyComponent = (
            <MailListItemTwistyButton
                densityMode={densityMode}
                isDarkTheme={isDarkTheme}
                isFirstLevelExpanded={isFirstLevelExpanded}
                renderMinimum={renderMinimum}
                isSecondLevelExpanded={isSecondLevelExpanded}
                isLoadingSecondLevelExpansion={isLoadingSecondLevelExpansion}
                isSelected={isSelected}
                isSingleLine={false}
                isUnread={unreadCount > 0}
                rowKey={rowKey}
                styleSelectorAsPerUserSettings={styleSelectorAsPerUserSettings}
                tableViewId={tableViewId}
            />
        );

        const renderFirstLineTextComponent = (text: string, tooltipText: string) => {
            return (
                <MailListItemFirstLineText
                    highlightTerms={highlightTerms}
                    showDraftsIndicator={showDraftsIndicator}
                    isInOutboxFolder={isInOutboxFolder}
                    unreadCount={unreadCount}
                    text={text}
                    tooltipText={tooltipText}
                    isSingleLineView={false}
                    highlightAccuracy={tableView?.highlightAccuracy}
                    isScheduledSend={isScheduledSend}
                    conditionalFormattingModifications={conditionalFormattingModifications}
                    isDarkTheme={isDarkTheme}
                    isUnread={isUnread}
                    isSelected={isSelected}
                />
            );
        };

        const renderSenderEmailLineTextComponent = (sender: SingleRecipientType) => {
            const senderText: string =
                !sender.Mailbox?.EmailAddress ||
                !isValidEmailAddress(sender.Mailbox?.EmailAddress ?? '')
                    ? `<${loc(senderEmailNotFoundLabel)}>`
                    : `<${sender.Mailbox.EmailAddress}>`;

            return (
                <MailListItemFirstLineText
                    highlightTerms={highlightTerms}
                    showDraftsIndicator={showDraftsIndicator}
                    isInOutboxFolder={isInOutboxFolder}
                    unreadCount={unreadCount}
                    text={senderText}
                    tooltipText={sender.Mailbox?.EmailAddress ?? ''}
                    isSingleLineView={false}
                    highlightAccuracy={tableView?.highlightAccuracy}
                    isScheduledSend={isScheduledSend}
                    isDarkTheme={isDarkTheme}
                    isUnread={isUnread}
                    isSelected={isSelected}
                />
            );
        };

        const renderCategoryWellComponent = (
            categoryWellContainerClass: string,
            categoryContainerClass: string
        ): JSX.Element => {
            return (
                <CategoryWell
                    key={rowId}
                    categoryWellContainerClass={categoryWellContainerClass}
                    categoryContainerClass={categoryContainerClass}
                    categories={categories}
                    onCategoryClicked={onCategoryClicked}
                    mailboxInfo={mailboxInfo}
                    conversationId={conversationId}
                />
            );
        };

        const iconBarComponent = (
            <MailListItemIconBar
                canArchive={canArchive}
                canDelete={canDelete}
                canPin={canPin}
                effectiveMentioned={effectiveMentioned}
                hasAttachment={hasAttachment}
                hasSharepointLink={hasSharepointLink}
                importance={importance}
                isComplete={isComplete}
                isFlagged={isFlagged}
                isInArchiveFolder={isInArchiveFolder}
                isInNotesFolder={isInNotesFolder}
                isInOutboxFolder={isInOutboxFolder}
                isPinned={isPinned}
                isRowExpanded={isFirstLevelExpanded || isSecondLevelExpanded}
                isSingleLine={false}
                isSnoozed={isSnoozed}
                isTaggedForBigScreen={isTaggedForBigScreen}
                itemClassIcon={itemClassIcon}
                lastDeliveryTimestamp={lastDeliveryTimestamp}
                neverShowHoverIcons={!supportsHoverIcons}
                neverShowPropertyIcons={false}
                rowKey={rowKey}
                smimeType={smimeType}
                supportsFlagging={supportsFlagging}
                supportsMove={supportsMove}
                supportsPinning={supportsPinning}
                tableViewId={tableViewId}
                unreadCount={unreadCount}
                isReminderSet={isReminderSet}
                hideImportancePropertyIcon={false}
            />
        );

        const _renderReactions = () => {
            if (!notExpanded) {
                return null;
            }

            return renderReactions({
                latestItemId,
                reactionsContainerClassName: reactionsContainer,
                rowId,
                tableViewId,
            });
        };

        const onPreviewClick = React.useCallback(
            (evt: React.MouseEvent<unknown> | React.TouchEvent<unknown>) => {
                onMailListItemClickHandler(
                    evt,
                    MailListItemSelectionSource.MailListItemRichPreview,
                    rowKey,
                    tableViewId
                );
            },
            [rowKey, tableViewId]
        );

        const attachmentPreviewWellViewState =
            getListViewStore().rowAttachmentPreviewWellViews.get(rowId);
        const previewViewState =
            attachmentPreviewWellViewState &&
            calculateAttachmentPreviewWellViewState(
                attachmentPreviewWellViewState,
                tableViewId,
                false /* isWideView */
            );

        const unreadBar = React.useMemo(() => {
            return (
                <MailListItemUnreadBar
                    isExpanded={isFirstLevelExpanded || isSecondLevelExpanded}
                    isUnread={isUnread}
                    rowKey={rowKey}
                    tableViewId={tableViewId}
                    isDarkTheme={isDarkTheme}
                    conditionalFormattingModifications={conditionalFormattingModifications}
                    cfPreviewUnreadBarVars={cfPreviewUnreadBarVars}
                />
            );
        }, [
            isFirstLevelExpanded,
            isSecondLevelExpanded,
            isUnread,
            rowKey,
            tableViewId,
            conditionalFormattingModifications,
        ]);

        let firstRowStyle;
        if (isUnread) {
            firstRowStyle = firstRowThreeColumnUnreadNext;
        } else {
            firstRowStyle = firstRowThreeColumnNext;
        }

        return (
            <>
                {unreadBar}
                <div
                    className={classnames(densityModeString, sclContainer)}
                    style={cfPreviewBackgroundColorStyle}
                >
                    <div
                        className={classnames(
                            firstColumnThreeColumn,
                            isConversationView &&
                                !shouldShowTwisty &&
                                firstColumnThreeColumnNoTwisty,
                            containerForTwistyAndCheckboxSCL,
                            densityMode === 'compact' && containerPaddingCompact,
                            !showCheckbox &&
                                getDensityModeCssClass(
                                    containerPaddingNoCheckboxFull,
                                    containerPaddingNoCheckboxMedium,
                                    undefined
                                ),
                            !shouldShowTwisty &&
                                showMultiSelectButton &&
                                !senderImageEnabled &&
                                !showCheckbox &&
                                multiSelectNoTwistyDisplayed
                        )}
                    >
                        {shouldShowTwisty && twistyComponent}
                        {showCheckbox && personaCheckboxComponent}
                    </div>
                    <div className={classnames(column, !showCheckbox && multiSelectNoCheckbox)}>
                        <div className={classnames(firstRowStyle, densityModeString)}>
                            <MailListItemMarker
                                isUnauthenticatedSender={isUnauthenticatedSender}
                                shouldShowExternalLabel={shouldShowExternalLabel}
                                isSelected={isSelected}
                                bulkCategory={bulkCategory}
                            />

                            {renderFirstLineTextComponent(
                                firstLineText,
                                firstLineTooltipText ?? ''
                            )}
                            {iconBarComponent}
                        </div>
                        {isInJunkFolder && lastSender && !isValidEmailAddress(firstLineText) ? (
                            <div>{renderSenderEmailLineTextComponent(lastSender)}</div>
                        ) : null}
                        <div
                            className={classnames(
                                isUnread ? secondRowThreeColumnUnread : secondRowThreeColumn,
                                !isUnread && secondRowThreeColumnRead,
                                secondRowThreeColumnPaddingNext
                            )}
                        >
                            <div
                                className={classnames(
                                    secondLineTextThreeColumn,
                                    densityModeString,
                                    useMessageListTextStyle('Major'),
                                    conversationHeaderContent
                                )}
                            >
                                {!showPreviewText && (
                                    <CopilotInboxPriorityIcon
                                        copilotInboxScore={copilotInboxScore}
                                        scenario={isPrioritySort ? 'PrioritySort' : 'ML'}
                                        mailboxInfo={mailboxInfo}
                                        isUnread={isUnread}
                                    />
                                )}
                                {showCategories &&
                                    showCategoriesInline &&
                                    renderCategoryWellComponent(
                                        categoryWellThreeColumn,
                                        categoryContainerThreeColumn
                                    )}
                                <MailListItemLineText
                                    addOverflowStyles={true}
                                    highlightTerms={highlightTerms}
                                    highlightAccuracy={tableView?.highlightAccuracy}
                                    text={secondLineText}
                                    tooltipText={secondLineTooltipText ?? ''}
                                    conditionalFormattingModifications={
                                        conditionalFormattingModifications
                                    }
                                    isDarkTheme={isDarkTheme}
                                    isSelected={isSelected}
                                />
                            </div>
                            <MailListItemUnreadCount
                                conditionalFormattingModifications={
                                    conditionalFormattingModifications
                                }
                                unreadCount={unreadCount}
                                isDarkTheme={isDarkTheme}
                            />
                            {!showPreviewText && _renderReactions()}
                            {isTaggedForBigScreen ? (
                                /* eslint-disable-next-line react/jsx-no-useless-fragment -- (https://aka.ms/OWALintWiki)
                                 * Don't use react fragments if there are no children
                                 *	> Fragments should contain more than one child - otherwise, there’s no need for a Fragment at all. */
                                <></> // TODO: Update this after get the proper message to display.
                            ) : returnTime ? (
                                <MailListItemTimestamp
                                    isPinned={isPinned}
                                    isSingleLine={false}
                                    isSnoozed={true}
                                    returnTime={returnTime}
                                    supportsPinning={supportsPinning}
                                    isCfPreview={isCfPreview}
                                    conditionalFormattingModifications={
                                        conditionalFormattingModifications
                                    }
                                    isDarkTheme={isDarkTheme}
                                    isSelected={isSelected}
                                />
                            ) : (
                                !isFirstLevelExpanded &&
                                !isSecondLevelExpanded && (
                                    <MailListItemTimestamp
                                        isPinned={isPinned}
                                        isSingleLine={false}
                                        isUnread={isUnread}
                                        lastDeliveryTimestamp={lastDeliveryTimestamp}
                                        returnTime={returnTime ?? ''}
                                        supportsPinning={supportsPinning}
                                        isCfPreview={isCfPreview}
                                        conditionalFormattingModifications={
                                            conditionalFormattingModifications
                                        }
                                        isDarkTheme={isDarkTheme}
                                        isSelected={isSelected}
                                    />
                                )
                            )}
                        </div>
                        {showPreviewTextConditionally && (
                            <div
                                className={classnames(previewDisplayThreeColumn, densityModeString)}
                            >
                                <div className={classnames(previewDisplayThreeColumnCategories)}>
                                    {showPreviewText && (
                                        <CopilotInboxPriorityIcon
                                            copilotInboxScore={copilotInboxScore}
                                            scenario={isPrioritySort ? 'PrioritySort' : 'ML'}
                                            mailboxInfo={mailboxInfo}
                                            isUnread={isUnread}
                                        />
                                    )}
                                    {showCategories &&
                                        !showCategoriesInline &&
                                        renderCategoryWellComponent(
                                            categoryWellThreeColumn,
                                            getDensityModeCssClass(
                                                categoryContainerThreeColumnFull,
                                                categoryContainerThreeColumnMediumOrCompact,
                                                categoryContainerThreeColumnMediumOrCompact
                                            )
                                        )}
                                    {showPreviews && (
                                        <MailListItemPreviewDisplay
                                            highlightTerms={highlightTerms}
                                            highlightAccuracy={tableView?.highlightAccuracy}
                                            previewDisplay={thirdLineText}
                                            rowId={rowId}
                                            mailboxInfo={mailboxInfo}
                                            cfTextStyle={conditionalFormattingTextStyle}
                                            isSelected={isSelected}
                                        />
                                    )}
                                </div>
                                {showFolderTag && (
                                    <div className={threeColFolderTag}>
                                        <FolderTag
                                            folderId={parentFolderId}
                                            parentFolderDisplayName={parentFolderDisplayName}
                                            isPreviewTextOn={showPreviewText}
                                            isSingleLine={false}
                                            mailboxGuids={mailboxGuids}
                                            shouldBeInTabOrder={isSelected}
                                        />
                                    </div>
                                )}
                                {_renderReactions()}
                            </div>
                        )}
                        {showLabelsContainer && (
                            <MailListItemLabelsContainer
                                categories={categories}
                                categoryContainerClass={categoryContainerThreeColumn}
                                categoryWellContainerClass={categoryWellThreeColumn}
                                isSingleLine={false}
                                mailboxGuids={mailboxGuids}
                                parentFolderDisplayName={parentFolderDisplayName}
                                parentFolderId={parentFolderId}
                                rowId={rowId}
                                showCategories={showCategories && notExpanded}
                                showFolderTag={showFolderTag}
                                showPreviewText={showPreviewText}
                                mailboxInfo={mailboxInfo}
                            />
                        )}
                        {showPreviews && showAttachmentPreview && previewViewState && (
                            <MailListItemRichPreviewWell
                                isWideView={false}
                                onPreviewClick={onPreviewClick}
                                tableViewId={tableViewId}
                                previewViewState={previewViewState}
                                attachmentPreviewWellViewState={attachmentPreviewWellViewState}
                            />
                        )}
                        {shouldShowRSVP && (
                            <MailListItemMeetingInfo
                                instrumentationContext={instrumentationContext}
                                conversationId={conversationId}
                                isSelected={isSelected}
                                isSingleLineView={false}
                                latestItemId={latestItemId}
                                listViewType={listViewType}
                                tableViewId={tableViewId}
                                rowKey={rowKey}
                            />
                        )}
                        {shouldSuggestUnsubscribe && (
                            <SuggestedUnsubscribe
                                isSingleLineView={false}
                                isSelected={isSelected}
                                rowId={rowId}
                                tableView={tableView}
                            />
                        )}
                    </div>
                </div>
            </>
        );
    },
    'MailListItemFullThreeColumn'
);
export default defaultExport;
